import {
  DownloadOutlined,
  EyeOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import { pdf } from '@react-pdf/renderer'
import { Alert, Button, message, Progress, Typography } from 'antd'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { PDFDocument } from 'features/reporting/pdfGeneration/PDFDocument'
import { PDFGenerationSection } from 'features/reporting/types'
import { useContextBridge } from 'its-fine'
import {
  RautReportContext,
  useRautReportOps,
} from 'pages/reporting/reports/raut/RautReportProvider'
import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'

const { Text } = Typography

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const PageWrapper = styled.div`
  padding: 1.5rem;
  max-width: 50rem;
`

const PageHeader = styled.div`
  margin-bottom: 2rem;
`

const PageTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  letter-spacing: -0.02em;
  line-height: 1.3;
`

const PageDescription = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  line-height: 1.5;
`

const SectionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const SectionCard = styled.div`
  background: ${({ theme }) =>
    theme.forecast.cardBackground || theme.forecast.background1};
  border: 1px solid
    ${({ theme }) =>
      theme.forecast.cardDividerBackground || theme.forecast.border};
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.5rem
    ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.06)'};
  padding: 1.5rem;
`

const SectionHeader = styled.div`
  margin-bottom: 1rem;
`

const SectionTitle = styled.h4`
  margin: 0 0 0.25rem 0;
  font-size: 1.0625rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  letter-spacing: -0.01em;
`

const SectionDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  line-height: 1.5;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1rem;
  background: ${({ theme }) =>
    theme.forecast.cardGroupBackground || theme.forecast.background2};
  border-radius: 0.5rem;
`

const LoadingIcon = styled(LoadingOutlined)`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.forecast.primary || '#1890ff'};
`

const LoadingProgress = styled(Progress)`
  width: 100%;
  max-width: 20rem;
`

const LoadingText = styled(Text)`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
`

const StyledPrimaryButton = styled(Button)`
  && {
    height: 2.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 0.5rem;
    transition: all 0.2s ease;

    &:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 0.25rem 0.75rem
        ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.15)'};
    }

    &:not(:disabled):active {
      transform: translateY(0);
    }
  }
`

const StyledSecondaryButton = styled(Button)`
  && {
    height: 2.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 0.5rem;
    background: ${({ theme }) => theme.forecast.defaultButton || 'transparent'};
    border: 1px solid
      ${({ theme }) =>
        theme.forecast.cardDividerBackground || theme.forecast.border};
    color: ${({ theme }) => theme.forecast.activeText};
    transition: all 0.2s ease;

    &:not(:disabled):hover {
      background: ${({ theme }) =>
        theme.forecast.hoverButton || 'rgba(0, 0, 0, 0.04)'};
      border-color: ${({ theme }) => theme.forecast.background4};
      color: ${({ theme }) => theme.forecast.activeText};
    }

    &:not(:disabled):active {
      background: ${({ theme }) =>
        theme.forecast.activeButton || 'rgba(0, 0, 0, 0.08)'};
    }
  }
`

const StyledDangerButton = styled(Button)`
  && {
    height: 2.5rem;
    font-weight: 500;
    font-size: 0.875rem;
    border-radius: 0.5rem;
    transition: all 0.2s ease;

    &:not(:disabled):hover {
      transform: translateY(-1px);
      box-shadow: 0 0.25rem 0.75rem
        ${({ theme }) => theme.forecast.boxShadow || 'rgba(0, 0, 0, 0.15)'};
    }

    &:not(:disabled):active {
      transform: translateY(0);
    }
  }
`

const TimestampText = styled(Text)`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.tertiaryText};
`

const AlertWrapper = styled.div`
  margin-top: 1rem;
`

const RenderReact: React.FC = () => {
  const contextValue = useRautReportOps()
  const {
    pdfState,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    handlePreviewLatestDraft,
    handlePreviewPublishedPdf,
    isSubmitting,
    report,
    metadataEntryId,
    formData,
    refreshMetadataEntry,
    refreshPdfState,
  } = contextValue

  const hasAtLeastOneSignature = Boolean(
    formData.signoff?.supervisorSignoff ||
    formData.signoff?.reviewerSignoff ||
    formData.signoff?.dataAnalystSignoff
  )

  // State for PDF generation
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState<string>('')
  // Store section pages outside of PDF Document so we can render the TOC with correct page numbers
  const initialSectionPagesRef = useRef<Record<string, number>>({})

  // API hooks
  const { rolodexUploadFile } = useRolodexEdit()

  const Bridge = useContextBridge()

  const handleOnPageRender = useCallback(
    ({ id, pageNumber }: { id: string; pageNumber: number }) => {
      setProgress((prev) => Math.min(prev + 8, 90))
      setCurrentStep(`Generating ${id} page...`)
      initialSectionPagesRef.current = {
        ...initialSectionPagesRef.current,
        [id]: pageNumber,
      }
    },
    [setProgress, setCurrentStep]
  )

  // Generate PDF
  const generatePDF = useCallback(async () => {
    setProgress(0)
    setCurrentStep('Preparing PDF document...')
    setProgress(20)

    // Yup, this looks a bit stupid, but what're ya gonna do?
    // The Table of Contents PDF is rendered along the other components, but we want it to know the final page numbers
    // Of the other PDf components. The simplest way to do this is to simply render twice!
    // First render populates our reference of initial section pages, and the second render creates the PDF with that page data.
    // PDF rendering should always be identical across these two renders.
    const initialPdfInstance = (
      <RautReportContext.Provider value={contextValue}>
        <Bridge>
          <PDFDocument
            report={report}
            onPageRender={handleOnPageRender}
            initialSectionPages={initialSectionPagesRef.current}
          />
        </Bridge>
      </RautReportContext.Provider>
    )

    setCurrentStep('Initializing PDF (pass 1)...')
    try {
      await pdf(initialPdfInstance).toBlob()
    } catch (pass1Error) {
      console.error('[PDFGeneration] First pass failed:', pass1Error)
      throw pass1Error
    }
    setProgress(20)

    const pdfInstance = (
      <RautReportContext.Provider value={contextValue}>
        <Bridge>
          <PDFDocument
            report={report}
            onPageRender={handleOnPageRender}
            initialSectionPages={initialSectionPagesRef.current}
          />
        </Bridge>
      </RautReportContext.Provider>
    )

    setCurrentStep('Finalizing PDF (pass 2)...')
    try {
      const blob = await pdf(pdfInstance).toBlob()
      setProgress(100)
      return blob
    } catch (pass2Error) {
      console.error('[PDFGeneration] Second pass failed:', pass2Error)
      throw pass2Error
    }
  }, [contextValue, report, Bridge, handleOnPageRender])

  // Generate and save PDF draft
  const handleGeneratePDF = useCallback(async () => {
    try {
      setIsGenerating(true)
      const pdfBlob = await generatePDF()

      const d = new Date()
      const pad = (n: number) => String(n).padStart(2, '0')
      const timestamp = `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`
      const path = `reportPDFDrafts/report-${metadataEntryId}-draft-${timestamp}.pdf`
      await rolodexUploadFile(metadataEntryId, pdfBlob, path)

      message.success('PDF generated successfully')
      await refreshPdfState()
      await refreshMetadataEntry()
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      console.error('Failed to generate PDF:', error)
      message.error(`Failed to generate PDF: ${errorMessage}`)
    } finally {
      setIsGenerating(false)
      setProgress(0)
      setCurrentStep('')
    }
  }, [
    generatePDF,
    metadataEntryId,
    rolodexUploadFile,
    refreshPdfState,
    refreshMetadataEntry,
  ])

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Generate PDF</PageTitle>
        <PageDescription>
          Generate and download the final report PDF. You can create a draft
          version for review or publish the final version.
        </PageDescription>
      </PageHeader>

      <SectionsContainer>
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Draft PDF</SectionTitle>
            <SectionDescription>
              Generate a draft PDF for review and internal use.
            </SectionDescription>
          </SectionHeader>

          {isGenerating ? (
            <LoadingContainer>
              <LoadingIcon />
              <LoadingProgress percent={progress} status='active' />
              <LoadingText>{currentStep}</LoadingText>
            </LoadingContainer>
          ) : (
            <ButtonGroup>
              <StyledPrimaryButton
                type='primary'
                icon={<SaveOutlined />}
                onClick={handleGeneratePDF}
                loading={isGenerating}
                disabled={isGenerating}>
                Generate PDF
              </StyledPrimaryButton>
              {pdfState.latestDraftTimestamp && (
                <>
                  <StyledSecondaryButton
                    icon={<FileTextOutlined />}
                    onClick={handleDownloadLatestDraft}
                    loading={isSubmitting}>
                    Download Latest Draft
                  </StyledSecondaryButton>
                  <StyledSecondaryButton
                    icon={<EyeOutlined />}
                    onClick={() => handlePreviewLatestDraft()}>
                    Preview Latest Draft
                  </StyledSecondaryButton>
                  <TimestampText>
                    Last generated:{' '}
                    {new Date(pdfState.latestDraftTimestamp).toLocaleString()}
                  </TimestampText>
                </>
              )}
            </ButtonGroup>
          )}
        </SectionCard>

        <SectionCard>
          <SectionHeader>
            <SectionTitle>Published PDF</SectionTitle>
            <SectionDescription>
              Publish and download the final official report PDF.
            </SectionDescription>
          </SectionHeader>

          <ButtonGroup>
            {!pdfState.hasPublishedPdf ? (
              <StyledDangerButton
                type='primary'
                danger
                icon={<DownloadOutlined />}
                onClick={() => publishLatestDraftPDF()}
                loading={pdfState.isPublishing}
                disabled={!hasAtLeastOneSignature}>
                Publish Final PDF
              </StyledDangerButton>
            ) : (
              <>
                <StyledPrimaryButton
                  type='primary'
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownloadPublishedPdf()}>
                  Download Published PDF
                </StyledPrimaryButton>
                <StyledSecondaryButton
                  icon={<EyeOutlined />}
                  onClick={handlePreviewPublishedPdf}>
                  Preview Published PDF
                </StyledSecondaryButton>
                <StyledSecondaryButton
                  onClick={() => publishLatestDraftPDF()}
                  loading={pdfState.isPublishing}
                  disabled={!hasAtLeastOneSignature}>
                  Update Published PDF
                </StyledSecondaryButton>
              </>
            )}
          </ButtonGroup>

          {!hasAtLeastOneSignature && (
            <AlertWrapper>
              <Alert
                title='Signatures Required'
                description='At least one signature must be added before the PDF can be published.'
                type='warning'
                showIcon
              />
            </AlertWrapper>
          )}
          {pdfState.hasPublishedPdf && (
            <AlertWrapper>
              <Alert
                title='PDF Published'
                description='The official report PDF has been published and is available for download.'
                type='success'
                showIcon
              />
            </AlertWrapper>
          )}
        </SectionCard>
      </SectionsContainer>
    </PageWrapper>
  )
}

export const PDFGeneration: PDFGenerationSection = {
  id: 'pdfGeneration',
  title: 'Generate PDF',
  description: 'Generate and download final report',
  RenderReact,
  required: false,
}
